import React, { useEffect, useRef, useState } from "react";
import { httpDelete } from "../helpers/httpMethods";
import "./cartDrawer.css";
import { useWallet, naira } from "../Wallet/walletOn";
import "../Wallet/wallet.css";

// Slide-in cart (bottom sheet on phones). Uses the same calls as before:
// my_carts list (from context), delete_cart/:id, AddToCart(amount, type, currency, publicationId),
// sendPaymentDetailsToBackend for one item, multipleCheckout for several, handeleClearCart to empty.
const PLANS = [
  { type: "one-off", label: "Today", fee: "oneOffFee", usd: "oneOffDollarFee", days: 1, what: "Today’s edition" },
  { type: "weekly", label: "Week", fee: "weeklyFee", usd: "weeklyDollarFee", days: 7, what: "Every edition for 7 days" },
  { type: "monthly", label: "Month", fee: "monthlyFee", usd: "monthlyDollarFee", days: 30, what: "Every edition for 30 days" },
];
const num = (v) => { const n = Number(String(v == null ? "" : v).replace(/[^0-9.]/g, "")); return Number.isFinite(n) ? n : 0; };
const titleCase = (s) => (s && s === s.toUpperCase() ? s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : String(s || "").trim());

export default function CartDrawer({ open, onClose, items, userLocation, onPayOne, onCheckoutAll, onAdd, onRefresh, onClear }) {
  const ng = userLocation === "Nigeria";
  const money = (v) => (ng ? `₦${Math.round(num(v)).toLocaleString("en-NG")}` : `$${num(v).toFixed(2)}`);
  const list = Array.isArray(items) ? items : [];
  const wallet = useWallet(); // "Pay from wallet" only when the wallet is switched on for this reader
  const [busy, setBusy] = useState("");
  const [undo, setUndo] = useState(null);
  const [err, setErr] = useState("");
  const timer = useRef(null);
  const panel = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    const key = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", key);
    setTimeout(() => panel.current && panel.current.focus(), 50);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", key); };
  }, [open]);
  useEffect(() => () => clearTimeout(timer.current), []);
  if (!open) return null;

  const priceOf = (it) => (ng ? num(it.amount) : num(it.amou || it.amount));
  const total = list.reduce((s, it) => s + priceOf(it), 0);
  const plansFor = (pub) => (ng ? PLANS.map((p) => ({ ...p, price: num(pub && pub[p.fee]) })).filter((p) => p.price > 0) : []);

  const remove = async (it) => {
    setBusy(it.id); setErr("");
    const r = await httpDelete(`delete_cart/${it.id}`);
    setBusy("");
    if (!r || r.er) { setErr((r && r.er && r.er.message) || "We couldn’t remove that. Please try again."); return; }
    onRefresh();
    clearTimeout(timer.current);
    setUndo(it);
    timer.current = setTimeout(() => setUndo(null), 7000);
  };
  const undoRemove = async () => {
    const it = undo; setUndo(null); clearTimeout(timer.current);
    if (it) await onAdd(it.amount, it.subscriptionType, it.currency, it.publication.id);
  };
  const changePlan = async (it, p) => {
    if (it.subscriptionType === p.type || busy) return;
    setBusy(it.id); setErr("");
    const r = await httpDelete(`delete_cart/${it.id}`);
    if (!r || r.er) { setBusy(""); setErr("We couldn’t change the plan. Please try again."); return; }
    await onAdd(String(p.price), p.type, it.currency || "NGN", it.publication.id);
    setBusy("");
  };
  const payFromWallet = () => {
    if (!list.length) return;
    onClose();
    onCheckoutAll(list, list.reduce((s, it) => s + num(it.amount), 0), "wallet");
  };
  const pay = () => {
    if (!list.length) return;
    onClose();
    if (list.length === 1) {
      const it = list[0];
      onPayOne(it.amount, it.subscriptionType, it.currency, it.publication.id, it.publication.isPaid);
    } else onCheckoutAll(list, list.reduce((s, it) => s + num(it.amount), 0));
  };
  // Savings tip: the item with the biggest saving from switching a day pass to a month
  const tip = list.map((it) => {
    const ps = plansFor(it.publication); const day = ps.find((p) => p.type === "one-off"); const month = ps.find((p) => p.type === "monthly");
    return it.subscriptionType === "one-off" && day && month && day.price * 30 > month.price ? { it, save: day.price * 30 - month.price, month } : null;
  }).filter(Boolean).sort((a, b) => b.save - a.save)[0];

  return (
    <div className="cd-wrap" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="cd-panel" role="dialog" aria-modal="true" aria-label="Your cart" tabIndex={-1} ref={panel}>
        <span className="cd-grab" aria-hidden="true" />
        <header className="cd-head">
          <h2>Your cart {list.length ? <span>({list.length})</span> : null}</h2>
          <button type="button" className="cd-x" onClick={onClose} aria-label="Close cart">✕</button>
        </header>

        {!list.length ? (
          <div className="cd-empty">
            <span aria-hidden="true">🗞️</span>
            <strong>Your cart is empty</strong>
            <p>Pick a paper for today, or subscribe and save.</p>
            <button type="button" className="cd-btn" onClick={() => { onClose(); if (window.location.pathname !== "/") window.location.href = "/"; }}>Browse today’s papers</button>
          </div>
        ) : (
          <>
            <div className="cd-body">
              {err ? <p className="cd-err" role="alert">{err}</p> : null}
              {list.map((it) => {
                const ps = plansFor(it.publication);
                const cur = PLANS.find((p) => p.type === it.subscriptionType) || PLANS[0];
                const day = ps.find((p) => p.type === "one-off");
                return (
                  <div className={`cd-item${busy === it.id ? " busy" : ""}`} key={it.id}>
                    <div className="cd-item-top">
                      <span className="cd-cover">{it.publication && it.publication.image ? <img src={it.publication.image} alt="" /> : null}</span>
                      <span className="cd-item-text">
                        <strong>{titleCase(it.publication && it.publication.name)}</strong>
                        <small>{cur.label === "Today" ? "Single issue" : `${cur.label}ly plan`.replace("Monthly plan", "Monthly plan")} · {cur.what}</small>
                        <button type="button" className="cd-link" onClick={() => remove(it)} disabled={!!busy}>Remove</button>
                      </span>
                      <b className="cd-price">{money(priceOf(it))}</b>
                    </div>
                    {ps.length > 1 ? (
                      <div className="cd-plans" role="radiogroup" aria-label={`Plan for ${titleCase(it.publication.name)}`}>
                        {ps.map((p) => {
                          const save = day && p.type !== "one-off" ? Math.round((1 - p.price / (day.price * p.days)) * 100) : 0;
                          const on = it.subscriptionType === p.type;
                          return (
                            <button type="button" key={p.type} role="radio" aria-checked={on} className={on ? "on" : ""} onClick={() => changePlan(it, p)} disabled={!!busy}>
                              <b>{p.label}</b><span>{money(p.price)}</span><em>{save > 0 ? `Save ${save}%` : "\u00a0"}</em>
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
              {tip ? (
                <button type="button" className="cd-tip" onClick={() => changePlan(tip.it, tip.month)} disabled={!!busy}>
                  <span aria-hidden="true">💡</span><span>Switch {titleCase(tip.it.publication.name)} to <b>Month</b> and save {money(tip.save)} compared with buying daily.</span>
                </button>
              ) : null}
              <a className="cd-gift" href="/account/gifts">🎁 Buying for someone else? Give it as a gift ›</a>
              <div className="cd-sum">
                {list.map((it) => <div key={it.id}><span>{titleCase(it.publication && it.publication.name)} · {(PLANS.find((p) => p.type === it.subscriptionType) || PLANS[0]).label}</span><span>{money(priceOf(it))}</span></div>)}
                <div className="cd-total"><span>Total</span><span>{money(total)}</span></div>
              </div>
            </div>
            <footer className="cd-foot">
              {ng && wallet.enabled && wallet.balance >= total && total > 0 ? (
                <>
                  <button type="button" className="cd-wallet" onClick={payFromWallet} disabled={!!busy}>Pay {money(total)} from wallet</button>
                  <small className="cd-wallet-note">Wallet balance {naira(wallet.balance)} · {naira(wallet.balance - total)} left after</small>
                  <button type="button" className="cd-link strong" onClick={pay} disabled={!!busy} style={{ alignSelf: "center" }}>Pay with card, transfer or USSD instead</button>
                </>
              ) : (
                <>
                  <button type="button" className="cd-btn cd-pay" onClick={pay} disabled={!!busy}>Pay {money(total)} securely</button>
                  <small>🔒 Card, transfer or USSD with Paystack · Read instantly after paying</small>
                  {ng && wallet.enabled && wallet.balance > 0 ? (
                    <small className="cd-wallet-note">Wallet {naira(wallet.balance)}: add {naira(total - wallet.balance)} more to pay from it. <button type="button" onClick={() => { onClose(); window.dispatchEvent(new Event("tns:wallet-open")); }}>Add money</button></small>
                  ) : null}
                </>
              )}
              <div className="cd-foot-links"><button type="button" className="cd-link strong" onClick={onClose}>← Keep browsing</button><button type="button" className="cd-link" onClick={() => { onClose(); onClear(); }}>Empty cart</button></div>
            </footer>
          </>
        )}
        {undo ? <div className="cd-undo" role="status"><span>{titleCase(undo.publication && undo.publication.name)} removed</span><button type="button" onClick={undoRemove}>Undo</button></div> : null}
      </aside>
    </div>
  );
}
